import { spawnSync } from "node:child_process";
import { writeFileSync } from "node:fs";
import { join } from "node:path";
import { tmpdir } from "node:os";

export interface ClipboardResult {
  ok: boolean;
  fallbackPath?: string;
}

/** Copy text to system clipboard. Returns true if successful. */
export function copyToClipboard(text: string): boolean {
  // macOS
  if (process.platform === "darwin") {
    const r = spawnSync("pbcopy", { input: text });
    return r.status === 0 && !r.error;
  }

  // Linux — try xclip, then xsel
  for (const [cmd, args] of [
    ["xclip", ["-selection", "clipboard"]],
    ["xsel", ["--clipboard", "--input"]],
  ] as [string, string[]][]) {
    const r = spawnSync(cmd, args, { input: text });
    if (r.status === 0 && !r.error) return true;
  }

  // Windows
  if (process.platform === "win32") {
    const r = spawnSync("clip", { input: text, shell: true });
    return r.status === 0 && !r.error;
  }

  return false;
}

/**
 * Copy to clipboard with a file fallback.
 * Returns the path of the fallback file if clipboard is unavailable, or null on success.
 */
export function copyToClipboardOrFile(text: string, filename = "cai-prompt.md"): { ok: boolean; fallbackPath: string | null } {
  if (copyToClipboard(text)) {
    return { ok: true, fallbackPath: null };
  }

  // Clipboard unavailable — write to temp file as fallback
  try {
    const fallbackPath = join(tmpdir(), filename);
    writeFileSync(fallbackPath, text, "utf-8");
    return { ok: false, fallbackPath };
  } catch {
    return { ok: false, fallbackPath: null };
  }
}
